import { useMemo, useState } from "react";
import { Button, Checkbox, ConfirmDialog, TextInput } from "@a2z/ui";
import type {
  SellerPermission,
  StoreInviteView,
  StoreMemberView,
  StoreRoleView,
} from "@/lib/api/types";
import { useStoreMembersQuery } from "@/modules/team/services/useStoreMembersQuery";
import { useStoreInvitesQuery } from "@/modules/team/services/useStoreInvitesQuery";
import { useStoreRolesQuery } from "@/modules/team/services/useStoreRolesQuery";
import { useCreateStoreInviteMutation } from "@/modules/team/services/useCreateStoreInviteMutation";
import { useRevokeStoreInviteMutation } from "@/modules/team/services/useRevokeStoreInviteMutation";
import { useUpdateStoreMemberRolesMutation } from "@/modules/team/services/useUpdateStoreMemberRolesMutation";
import { useRemoveStoreMemberMutation } from "@/modules/team/services/useRemoveStoreMemberMutation";
import { useHasPermission } from "@/modules/team/hooks/useHasPermission";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  Card,
  Icons,
  ModalShell,
  tokens,
} from "@/modules/settings/components/redesign/store-settings/primitives";
import {
  systemRoleDesc,
  systemRoleName,
} from "./rbac-labels";

const ps = keys.seller.profileSettings;

// Pragmatic email shape check (server revalidates) — replaces the old
// `includes("@")` gate that accepted "a@b", "@@@@", etc.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Paper-plane send glyph — used as the trailing icon on the "Send invite"
// button so the action reads as "send", not "add".
function PaperPlaneIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4z" />
    </svg>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function extractErrorMessage(error: unknown): string {
  if (!error) return "";
  if (error instanceof Error) return error.message;
  return String(error);
}

export function TeamPanel() {
  const t = useTranslations();
  const storeSlug = useSellerStoreSlug();
  const { data: session } = useSessionQuery({ storeSlug });
  const has = useHasPermission(storeSlug);

  const { data: members = [], isLoading: membersLoading } =
    useStoreMembersQuery(storeSlug);
  const { data: invites = [], isLoading: invitesLoading } =
    useStoreInvitesQuery(storeSlug);
  const { data: roles = [], isLoading: rolesLoading } =
    useStoreRolesQuery(storeSlug);

  const updateRoles = useUpdateStoreMemberRolesMutation();
  const removeMember = useRemoveStoreMemberMutation();
  const revokeInvite = useRevokeStoreInviteMutation();

  const [inviteOpen, setInviteOpen] = useState(false);
  // Member pending removal — drives the confirm dialog (replaces native confirm).
  const [removing, setRemoving] = useState<StoreMemberView | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const confirmRemove = async (): Promise<void> => {
    if (!removing) return;
    try {
      await removeMember.mutateAsync({ storeSlug, memberId: removing.id });
      setRemoving(null);
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    }
  };

  const currentSellerId = session?.seller?.id;
  const pendingInvites = useMemo(
    () =>
      invites.filter(
        (invite) => invite.acceptedAt === null && invite.revokedAt === null,
      ),
    [invites],
  );

  const canInvite = has("MEMBER_INVITE");
  const canEditRoles = has("MEMBER_EDIT_ROLES");
  const canRemove = has("MEMBER_REMOVE");

  return (
    <Card
      title={t(ps.teamMembers)}
      desc={t(ps.teamMembersDesc)}
      action={
        canInvite ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => setInviteOpen(true)}
            trailingIcon={<span style={{ fontSize: 14 }}>{"+"}</span>}
          >
            {t(ps.inviteMember)}
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {membersLoading ? (
          <div style={{ fontSize: 12.5, color: tokens.text3 }}>
            {t(ps.loading)}
          </div>
        ) : members.length === 0 ? (
          <div style={{ fontSize: 12.5, color: tokens.text3 }}>
            {t(ps.noMembers)}
          </div>
        ) : (
          members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              roles={roles}
              isYou={member.sellerId === currentSellerId}
              canEditRoles={canEditRoles}
              canRemove={canRemove}
              busy={removeMember.isPending && removing?.id === member.id}
              onUpdateRoles={async (roleIds) => {
                await updateRoles.mutateAsync({
                  storeSlug,
                  memberId: member.id,
                  roleIds,
                });
              }}
              onRemove={() => setRemoving(member)}
            />
          ))
        )}

        {pendingInvites.length > 0 ? (
          <>
            <div
              style={{
                marginTop: 12,
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: tokens.text3,
              }}
            >
              {t(ps.pendingInvites)}
            </div>
            {pendingInvites.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                canRevoke={canInvite}
                busy={revokingId === invite.id}
                onRevoke={async () => {
                  setRevokingId(invite.id);
                  try {
                    await revokeInvite.mutateAsync({
                      storeSlug,
                      inviteId: invite.id,
                    });
                  } catch {
                    // Error is surfaced globally via the React Query MutationCache handler.
                  } finally {
                    setRevokingId(null);
                  }
                }}
              />
            ))}
          </>
        ) : null}
      </div>

      {inviteOpen ? (
        <InviteModal
          storeSlug={storeSlug}
          roles={roles}
          rolesLoading={rolesLoading || invitesLoading}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        isOpen={removing !== null}
        title={t(keys.common.remove)}
        body={removing ? t(ps.removeConfirm, { name: removing.sellerName }) : ""}
        confirmLabel={t(keys.common.remove)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={removeMember.isPending}
        intent="danger"
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </Card>
  );
}

type MemberRowProps = {
  member: StoreMemberView;
  roles: StoreRoleView[];
  isYou: boolean;
  canEditRoles: boolean;
  canRemove: boolean;
  busy?: boolean;
  onUpdateRoles: (roleIds: string[]) => Promise<void>;
  onRemove: () => void;
};

function MemberRow({
  member,
  roles,
  isYou,
  canEditRoles,
  canRemove,
  busy = false,
  onUpdateRoles,
  onRemove,
}: MemberRowProps) {
  const t = useTranslations();
  const [editing, setEditing] = useState(false);
  const isOwner = member.roles.some((role) => role.key === "OWNER");
  const rowClickable = canEditRoles && !isOwner && !isYou;
  const openEditor = (): void => {
    if (rowClickable) setEditing(true);
  };

  return (
    <div
      role={rowClickable ? "button" : undefined}
      tabIndex={rowClickable ? 0 : undefined}
      onClick={rowClickable ? openEditor : undefined}
      onKeyDown={
        rowClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openEditor();
              }
            }
          : undefined
      }
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        rowGap: 8,
        padding: 12,
        borderRadius: 10,
        border: `1px solid ${tokens.border}`,
        background: tokens.surface,
        cursor: rowClickable ? "pointer" : "default",
        transition: "background 0.12s, border-color 0.12s",
      }}
      onMouseEnter={
        rowClickable
          ? (e) => {
              e.currentTarget.style.background = tokens.bgSoft;
              e.currentTarget.style.borderColor = tokens.borderStrong;
            }
          : undefined
      }
      onMouseLeave={
        rowClickable
          ? (e) => {
              e.currentTarget.style.background = tokens.surface;
              e.currentTarget.style.borderColor = tokens.border;
            }
          : undefined
      }
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: tokens.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 700,
          fontSize: 12,
          flexShrink: 0,
        }}
      >
        {initials(member.sellerName)}
      </div>
      <div style={{ flex: 1, minWidth: "min(180px, 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>
            {member.sellerName}
          </span>
          {isYou ? (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 4,
                background: tokens.bgSoft,
                color: tokens.text3,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {t(ps.you)}
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 11.5, color: tokens.text3, marginTop: 1 }}>
          {member.sellerEmail}
        </div>
      </div>
      <RoleChips assigned={member.roles} />
      {editing ? (
        <MemberRolesEditorModal
          member={member}
          roles={roles}
          onClose={() => setEditing(false)}
          onSave={async (roleIds) => {
            await onUpdateRoles(roleIds);
            setEditing(false);
          }}
        />
      ) : null}
      {canRemove && !isOwner && !isYou ? (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="max-lg:min-h-10"
          style={{
            color: tokens.danger,
            borderColor: "oklch(0.85 0.08 25)",
          }}
        >
          {t(keys.common.remove)}
        </Button>
      ) : null}
    </div>
  );
}

type RoleChipsProps = {
  assigned: StoreMemberView["roles"];
};

function RoleChips({ assigned }: RoleChipsProps) {
  const t = useTranslations();
  const locale = useLocale();
  if (assigned.length === 0) {
    return (
      <span
        style={{
          fontSize: 11.5,
          color: tokens.text3,
          fontStyle: "italic",
        }}
      >
        {t(ps.noRoles)}
      </span>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        justifyContent: "flex-end",
        maxWidth: "min(280px, 100%)",
      }}
    >
      {assigned.map((role) => {
        const isOwner = role.key === "OWNER";
        return (
          <span
            key={role.id}
            title={role.key}
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              padding: "3px 8px",
              borderRadius: 999,
              background: isOwner
                ? `color-mix(in oklab, ${tokens.accent} 14%, white)`
                : tokens.bgSoft,
              color: isOwner ? tokens.accent2 : tokens.text2,
              border: `1px solid ${
                isOwner
                  ? `color-mix(in oklab, ${tokens.accent} 28%, white)`
                  : tokens.border
              }`,
            }}
          >
            {systemRoleName(role.key, locale, role.name)}
          </span>
        );
      })}
    </div>
  );
}

type MemberRolesEditorModalProps = {
  member: StoreMemberView;
  roles: StoreRoleView[];
  onClose: () => void;
  onSave: (roleIds: string[]) => Promise<void>;
};

function MemberRolesEditorModal({
  member,
  roles,
  onClose,
  onSave,
}: MemberRolesEditorModalProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(member.roles.map((role) => role.id)),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (roleId: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const valid = selected.size > 0;

  const submit = async () => {
    if (!valid) return;
    setError(null);
    setSaving(true);
    try {
      await onSave([...selected]);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : String(caught),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      icon={Icons.team}
      title={t(ps.editRolesFor, { name: member.sellerName })}
      subtitle={t(ps.rolesStack)}
      onClose={onClose}
      onSave={submit}
      valid={valid && !saving}
      saveLabel={saving ? t(ps.saving) : t(ps.saveRoles)}
      footerHint={
        error ? (
          <span style={{ color: tokens.danger, fontSize: 12 }}>{error}</span>
        ) : !valid ? (
          <span style={{ color: tokens.text3, fontSize: 12 }}>
            {t(ps.selectOneRole)}
          </span>
        ) : null
      }
      maxWidth={520}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {roles.length === 0 ? (
          <div style={{ fontSize: 12.5, color: tokens.text3 }}>
            {t(ps.noRolesDefined)}
          </div>
        ) : (
          roles.map((role) => {
            const checked = selected.has(role.id);
            return (
              <label
                key={role.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: 10,
                  borderRadius: 8,
                  border: `1px solid ${
                    checked ? tokens.accent : tokens.border
                  }`,
                  background: checked
                    ? `color-mix(in oklab, ${tokens.accent} 6%, white)`
                    : tokens.surface,
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => toggle(role.id)}
                  className="mt-0.5"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      color: tokens.text,
                    }}
                  >
                    {systemRoleName(role.key, locale, role.name)}
                    {role.isSystem ? (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "1px 5px",
                          borderRadius: 4,
                          background: tokens.bgSoft,
                          color: tokens.text3,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        {t(ps.system)}
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: tokens.text3,
                      marginTop: 2,
                    }}
                  >
                    {systemRoleDesc(role.key, locale, role.description) ??
                      t(ps.permissionsCount, {
                        count: role.permissions.length,
                      })}
                  </div>
                </div>
              </label>
            );
          })
        )}
      </div>
    </ModalShell>
  );
}

type InviteRowProps = {
  invite: StoreInviteView;
  canRevoke: boolean;
  busy?: boolean;
  onRevoke: () => Promise<void>;
};

function InviteRow({ invite, canRevoke, busy = false, onRevoke }: InviteRowProps) {
  const t = useTranslations();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 12,
        borderRadius: 10,
        border: `1px dashed ${tokens.border}`,
        background: tokens.bgSoft,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{invite.email}</div>
        <div
          style={{
            fontSize: 11.5,
            color: tokens.text3,
            marginTop: 1,
          }}
        >
          {t(ps.invitedAs, {
            roles: invite.roles.map((role) => role.name).join(", ") || "—",
            date: new Date(invite.expiresAt).toLocaleDateString(),
          })}
        </div>
      </div>
      {canRevoke ? (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void onRevoke()}
        >
          {t(ps.revoke)}
        </Button>
      ) : null}
    </div>
  );
}

type InviteModalProps = {
  storeSlug: string;
  roles: StoreRoleView[];
  rolesLoading: boolean;
  onClose: () => void;
};

function InviteModal({
  storeSlug,
  roles,
  rolesLoading,
  onClose,
}: InviteModalProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [email, setEmail] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(
    () => new Set(),
  );
  const create = useCreateStoreInviteMutation();

  // Owner is excluded from invite role choices: a fresh OWNER must be
  // promoted from an existing member, not parachuted in via email.
  const assignableRoles = roles.filter((role) => role.key !== "OWNER");

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((previous) => {
      const next = new Set(previous);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const valid =
    EMAIL_RE.test(email.trim().toLowerCase()) && selectedRoleIds.size > 0;

  const submit = async () => {
    try {
      await create.mutateAsync({
        storeSlug,
        email: email.trim(),
        roleIds: [...selectedRoleIds],
      });
      onClose();
    } catch {
      // Error surfaces via `create.error`; the form stays open so the user
      // can correct the input.
    }
  };

  return (
    <ModalShell
      icon={Icons.mail}
      title={t(ps.inviteMember)}
      subtitle={t(ps.inviteEmailSubtitle)}
      onClose={onClose}
      onSave={submit}
      valid={valid && !create.isPending}
      saveLabel={
        create.isPending ? (
          t(ps.sending)
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t(ps.sendInvite)}
            <PaperPlaneIcon />
          </span>
        )
      }
      footerHint={
        create.error ? (
          <span style={{ color: tokens.danger, fontSize: 12 }}>
            {extractErrorMessage(create.error)}
          </span>
        ) : selectedRoleIds.size === 0 ? (
          <span style={{ color: tokens.text3, fontSize: 12 }}>
            {t(ps.pickOneRole)}
          </span>
        ) : null
      }
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: tokens.text2 }}>
            {t(ps.emailLabel)}
          </span>
          <TextInput
            name="invite-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t(ps.emailPlaceholder)}
            autoFocus
            dir="ltr"
          />
        </label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: tokens.text2 }}>
            {t(ps.rolesCount, {
              selected: selectedRoleIds.size,
              total: assignableRoles.length,
            })}
          </span>
          {rolesLoading ? (
            <div style={{ fontSize: 12.5, color: tokens.text3 }}>
              {t(ps.loadingRoles)}
            </div>
          ) : assignableRoles.length === 0 ? (
            <div style={{ fontSize: 12.5, color: tokens.text3 }}>
              {t(ps.noRolesAvailable)}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                maxHeight: 260,
                overflowY: "auto",
                padding: 4,
              }}
            >
              {assignableRoles.map((role) => {
                const checked = selectedRoleIds.has(role.id);
                return (
                  <label
                    key={role.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: 10,
                      borderRadius: 8,
                      border: `1px solid ${
                        checked ? tokens.accent : tokens.border
                      }`,
                      background: checked
                        ? `color-mix(in oklab, ${tokens.accent} 6%, white)`
                        : tokens.surface,
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleRole(role.id)}
                      className="mt-0.5"
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: tokens.text,
                        }}
                      >
                        {systemRoleName(role.key, locale, role.name)}
                        {role.isSystem ? (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: "1px 5px",
                              borderRadius: 4,
                              background: tokens.bgSoft,
                              color: tokens.text3,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          >
                            {t(ps.system)}
                          </span>
                        ) : null}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: tokens.text3,
                          marginTop: 2,
                        }}
                      >
                        {systemRoleDesc(role.key, locale, role.description) ??
                          t(ps.permissionsCount, {
                            count: role.permissions.length,
                          })}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <span style={{ fontSize: 11, color: tokens.text3 }}>
            {t(ps.adjustLater)}
          </span>
        </div>
      </div>
    </ModalShell>
  );
}

// Re-export for places that referenced the old stub by name.
export type { SellerPermission };
